import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { GenericErrorMessage } from 'src/components/BeaconV2Components/common/CommonSnackbarMessages/GenericErrorMessage';
import { TopicFilter } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/constants';
import {
  topicSettingsServerProxy,
  UpdateTopicPayload
} from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/serverProxy';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';
import { TopicWithOwner } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/types';
import { usePollingContext } from 'src/providers/PollingContextProvider';
import { useSnackbar } from 'src/utils/Hooks';
import { useBeaconClientId, useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import React from 'react';

/**
 * Mutation hook for creating or updating a topic.
 * Handles side effects such as navigation, polling, and snackbar notifications.
 */
export const useTopicMutation = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { beaconNavigator } = useBeaconNavigator();
  const beaconClientId = useBeaconClientId();
  const retailerId = useRetailerId();
  const { startPolling, stopPolling } = usePollingContext();

  return useMutation({
    mutationFn: async ({
      name,
      filters,
      topic,
      edit
    }: {
      name: string;
      filters: Record<string, any[]>;
      topic?: TopicWithOwner;
      edit?: boolean;
    }) => {
      const activeFilters = Object.entries(filters)
        .filter(([_, values]) => values.length)
        .reduce((acc, [key, values]) => {
          acc[key as TopicFilter] = values;
          return acc;
        }, {});

      // Construct appropriate payload based on mode
      const payload = edit
        ? {
          topicName: name.length ? name : topic.topicName,
          topicId: topic.topicId,
          retailerId,
          beaconClientId,
          ...activeFilters
        }
        : {
          topicName: name,
          retailerId,
          ...activeFilters
        };

      // Make API call based on mode
      const topicId = await (edit
        ? topicSettingsServerProxy.updateTopic(payload as UpdateTopicPayload)
        : topicSettingsServerProxy.createTopic(payload));

      // Immediately navigate to topics list while processing
      beaconNavigator.setPathname('/home');
      beaconNavigator.updateSearchParams({
        tab: ['summary'],
        subtab: ['beacon'],
        entityType: ['searchtermlist']
      });
      beaconNavigator.go();

      // Show initial notification
      showSnackbar({
        type: 'info',
        message: 'We received your topic submission and will notify you when the processing is complete.'
      });

      // Start polling for completion
      const pollingKey = `topicCreation-${topicId}`;

      startPolling(
        pollingKey,
        async () => {
          const response = await topicSettingsServerProxy.getTopic(topicId);

          if (response?.status === 'Active') {
            stopPolling(pollingKey);
            // Invalidate all topic-related queries when the topic becomes active
            queryClient.invalidateQueries(TOPIC_QUERY_KEYS.all);

            // Show success message with View Topic button that navigates to topic entity
            setTimeout(() => {
              showSnackbar({
                type: 'success',
                showButton: true,
                buttonProps: {
                  text: 'View Topic',
                  onClick: () => {
                    // Trigger cache invalidation without waiting
                    topicSettingsServerProxy.invalidateCache();

                    beaconNavigator.setPathname(`/topic/${topicId}`);
                    beaconNavigator.updateSearchParams({
                      tab: ['traffic'],
                      subtab: ['totalClicks']
                    });
                    beaconNavigator.go();
                  }
                },
                message: `Topic ${edit ? 'updated' : 'created'} successfully.`,
                timeout: null
              });
            }, 2000);
          }
        },
        { interval: 5000, leading: true, maxTries: 15 }
      );

      return topicId;
    },
    onError: (error) => {
      console.error('Failed to handle topic:', error);
      showSnackbar({
        message: <GenericErrorMessage />,
        type: 'error'
      });
    }
  });
};
